// Brief B5 P0 Bug 4 — Booking modal on profile.
//
// Replaces the previous 4-step wizard (/app/booking/:userId) that started with
// a confusing "types de sessions" picker. Clicking "Réserver" on a profile now
// opens this modal in-place :
//   • LEFT  → liste des prochaines sessions publiques (cards date/heure/titre/CTA)
//   • RIGHT → calendrier (vue Semaine) de l'expert avec créneaux cliquables
// Both sides converge on the same auto-book flow: /event/{event_id}?book=1
// (plus &slot=<ISO> for virtual sub-slots from availability_schedules).
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { parseUtc } from '../lib/calendarDate';
import { useNavigate } from 'react-router-dom';
import { X, Calendar, Clock, ArrowRight, Loader2, Users } from 'lucide-react';
import PublicExpertCalendar from './PublicExpertCalendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return parseUtc(iso).toLocaleString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const SessionCard = ({ event, onBook }) => {
  const start = event.start_time && parseUtc(event.start_time);
  const isFull = event.max_capacity && (event.participants_count || 0) >= event.max_capacity;
  return (
    <div
      className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 hover:border-purple-300 hover:shadow-sm transition-all"
      data-testid={`booking-modal-session-${event.event_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{event.title}</p>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
            {start && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(event.start_time)}
              </span>
            )}
            {event.duration_minutes && (
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{event.duration_minutes} min</span>
            )}
            {event.max_capacity > 1 && (
              <span className="inline-flex items-center gap-1">
                <Users className="h-3 w-3" />{event.participants_count || 0}/{event.max_capacity}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onBook(event)}
          disabled={isFull}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ backgroundColor: isFull ? '#9ca3af' : '#7F77DD' }}
          data-testid={`booking-modal-book-${event.event_id}`}
        >
          {isFull ? 'Complet' : <>Réserver <ArrowRight className="h-3 w-3" /></>}
        </button>
      </div>
    </div>
  );
};

const BookingModal = ({ isOpen, onClose, expertUserId, expertName }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !expertUserId) return;
    setLoading(true);
    axios
      .get(`${API}/calendar/events/public/${expertUserId}`)
      .then(({ data }) => {
        const list = Array.isArray(data.events) ? data.events : [];
        setEvents(list);
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [isOpen, expertUserId]);

  // Brief B5 P0 Bug 4 — list concrete future sessions only :
  //   • public_event / public_slot rows from calendar_events (non-virtual)
  //   • exclude past
  //   • sort ascending by start_time
  //   • limit 12 (the calendar shows everything else)
  const futureSessions = useMemo(() => {
    const now = Date.now();
    return events
      .filter((e) => !e._virtual)
      .filter((e) => ['public_event', 'public_slot'].includes(e.type))
      .filter((e) => e.start_time && parseUtc(e.start_time).getTime() > now)
      .sort((a, b) => parseUtc(a.start_time) - parseUtc(b.start_time))
      .slice(0, 12);
  }, [events]);

  // Brief LOT1 — quand l'expert n'a que des disponibilités récurrentes (aucune
  // session concrète programmée), la liste propose les prochains créneaux
  // virtuels réservables au lieu d'un état vide.
  const virtualSlots = useMemo(() => {
    const now = Date.now();
    return events
      .filter((e) => e._virtual && e.slot_start_time)
      .filter((e) => e.start_time && parseUtc(e.start_time).getTime() > now)
      .sort((a, b) => parseUtc(a.start_time) - parseUtc(b.start_time))
      .slice(0, 8);
  }, [events]);

  const listedSessions = futureSessions.length > 0 ? futureSessions : virtualSlots;

  if (!isOpen) return null;

  const handleBook = (event) => {
    if (!event?.event_id) return;
    const slot = event._virtual && event.slot_start_time
      ? `&slot=${encodeURIComponent(event.slot_start_time)}`
      : '';
    onClose();
    navigate(`/event/${event.event_id}?book=1${slot}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="booking-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-5xl max-h-[90vh] rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">
              Réserver une session{expertName ? ` avec ${expertName}` : ''}
            </h2>
            <p className="text-[12px] text-gray-500 dark:text-gray-400">
              Choisis une session dans la liste ou un créneau sur le calendrier.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
            data-testid="booking-modal-close"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* LEFT — list of future sessions */}
          <section className="lg:col-span-2 space-y-3" data-testid="booking-modal-sessions-list">
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
              {futureSessions.length > 0
                ? `Prochaines sessions (${futureSessions.length})`
                : `Prochains créneaux (${virtualSlots.length})`}
            </h3>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-purple-500" />
              </div>
            ) : listedSessions.length === 0 ? (
              <p className="text-sm text-gray-500 italic p-4 rounded-lg bg-gray-50 dark:bg-gray-800/40 text-center">
                Aucune session publique programmée pour l'instant.
                <br />Regarde le calendrier pour les créneaux disponibles.
              </p>
            ) : (
              <div className="space-y-2.5">
                {listedSessions.map((ev) => (
                  <SessionCard
                    key={ev._virtual ? `${ev.event_id}_${ev.slot_start_time}` : ev.event_id}
                    event={ev}
                    onBook={handleBook}
                  />
                ))}
              </div>
            )}
          </section>

          {/* RIGHT — calendar. Cliquer un créneau ouvre EventDetailsModal qui
              propose le CTA "Réserver cette session" (Phase B2 §1). */}
          <section className="lg:col-span-3" data-testid="booking-modal-calendar">
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-3">
              Calendrier
            </h3>
            <PublicExpertCalendar userId={expertUserId} embedded />
          </section>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
